import { useActiveAddress } from "@arweave-wallet-kit/react"
import { Divider, Stack, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { useContext, useMemo } from "react"

import { ErrorBoundary } from "./ErrorBoundary"
import { OrderRow } from "./OrderRow"

import { getActivity } from "../ao/activity-api"
import { getSimpleAgent, getSimpleAgentInfo } from "../ao/agent"
import { getActiveOrders, OrderAgent } from "../ao/agents-api"
import { useAo } from "../ao/ao"
import { ConfigContext } from "../contexts/config"
import { RECENT_ACTIVITY } from "../settings"
import { createLogger } from "../utils/logger"

const logger = createLogger("ActiveOrders")

export function ActiveOrders() {
  // active address
  const activeAddr = useActiveAddress()

  // config
  const [config] = useContext(ConfigContext)

  // ao instance
  const ao = useAo()

  const { data: orders, refetch: refreshActiveOrders } = useQuery({
    queryKey: [`active_orders_${activeAddr}`, activeAddr, config],
    enabled: !!activeAddr,
    queryFn: async () => {
      if (!activeAddr) throw new Error("Params missing")

      const result = await getActiveOrders(activeAddr, ao)
      logger.log("onActiveOrders", result)
      return result
    },
  })

  const { refetch: refreshActivity } = useQuery({
    queryKey: [`activity_${activeAddr}`, activeAddr, config],
    enabled: !!activeAddr,
    queryFn: async () => {
      if (!activeAddr) throw new Error("Params missing")

      const [, result] = await getActivity(RECENT_ACTIVITY, undefined, false, activeAddr)
      logger.log("onPastActivities", result)
      return result
    },
  })

  const { data: hopperAgentId } = useQuery({
    queryKey: ["Get-Simple-Agent", activeAddr],
    queryFn: async () => {
      const id = await getSimpleAgent(activeAddr as string, ao)
      logger.log("onHopperAgent", id)
      return id
    },
    enabled: !!ao && !!activeAddr,
  })

  const { data: hopperAgentInfo } = useQuery({
    queryKey: ["Get-Simple-Agent-Info", hopperAgentId, ao],
    queryFn: async () => {
      const info = await getSimpleAgentInfo(hopperAgentId as string, ao)
      logger.log("onHopperAgentInfo", info)
      return info
    },
    enabled: !!ao && !!hopperAgentId,
  })

  const hopperOrder = useMemo(() => {
    if (!hopperAgentId || !hopperAgentInfo) return null
    if (!hopperAgentInfo.CurrentPath) return null

    try {
      const path = JSON.parse(hopperAgentInfo.CurrentPath) as string[]

      const order: OrderAgent = {
        amm_process: "",
        type: "hopper-order",
        token_to: path[path.length - 1],
        token_from: path[0],
        id: hopperAgentId,
        order_owner: hopperAgentInfo["Initiator"],
        // input: "0",
        // pending: 0
        // min_output: 0
        // expires_at_ts: 0
        // created_at_ts: 0
      }
      return order
    } catch {
      return null
    }
  }, [hopperAgentInfo, hopperAgentId])

  const allOrders = useMemo(() => {
    if (!orders) return []

    return hopperOrder ? [hopperOrder, ...orders] : orders
  }, [orders, hopperOrder])

  if (allOrders.length === 0) return null

  return (
    <>
      <Divider />
      <Typography>Active Orders</Typography>
      <Stack gap={1}>
        {allOrders.map((order) => (
          <ErrorBoundary key={order.id} fallback={<>Something went wrong</>}>
            <OrderRow
              order={order}
              refreshActiveOrders={refreshActiveOrders}
              refreshActivity={refreshActivity}
            />
          </ErrorBoundary>
        ))}
      </Stack>
    </>
  )
}
